import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enable credentials (cookies) for all requests
axios.defaults.withCredentials = true;

// Helper to get current tenant slug from URL path
const getTenantSlugFromPath = () => {
  const pathParts = window.location.pathname.split('/');
  // URL format: /:tenantSlug/dashboard, /:tenantSlug/templates, etc.
  if (pathParts.length >= 2 && pathParts[1] && pathParts[1] !== 'master' && pathParts[1] !== 'login' && pathParts[1] !== 'register' && pathParts[1] !== 'complete' && pathParts[1] !== 'portal' && pathParts[1] !== 'embed') {
    return pathParts[1];
  }
  return null;
};

// Axios interceptor to include tenant context header for Master Admin impersonation
axios.interceptors.request.use((config) => {
  const urlTenantSlug = getTenantSlugFromPath();
  if (urlTenantSlug) {
    // Add X-Tenant-Context header for backend to use when determining tenant_id
    config.headers['X-Tenant-Context'] = urlTenantSlug;
  }
  return config;
});

// Utility to extract error message from API error response
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  if (detail?.msg) {
    return detail.msg;
  }
  return fallback;
};

// Templates API
export const templatesApi = {
  list: () => axios.get(`${API_URL}/templates`),
  get: (id) => axios.get(`${API_URL}/templates/${id}`),
  create: (formData) => axios.post(`${API_URL}/templates`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => axios.put(`${API_URL}/templates/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => axios.delete(`${API_URL}/templates/${id}`),
  duplicate: (id) => axios.post(`${API_URL}/templates/${id}/duplicate`),
  detectFields: (id, useAi = true) => axios.post(`${API_URL}/templates/${id}/detect-fields`, { use_ai: useAi }),
  getDetectionStatus: (id, taskId) => axios.get(`${API_URL}/templates/${id}/detect-fields/status/${taskId}`),
  applyDetectedFields: (id, fields) => axios.post(`${API_URL}/templates/${id}/apply-detected-fields`, fields),
  testMapping: (id, bullhornId, entityType) => axios.post(`${API_URL}/templates/${id}/test-mapping`, { bullhorn_id: bullhornId, entity_type: entityType }),
};

// Auth API
export const authApi = {
  getBullhornTenants: () => axios.get(`${API_URL}/auth/bullhorn/tenants`),
  startBullhornLogin: (tenantSlug) => axios.get(`${API_URL}/auth/bullhorn/login`, { params: { tenant_slug: tenantSlug } }),
  completeBullhornLogin: (code, state) => axios.post(`${API_URL}/auth/bullhorn/callback`, null, { params: { code, state } }),
  bullhornDirectLogin: (username, password) => axios.post(`${API_URL}/auth/bullhorn/direct-login`, { username, password }),
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API_URL}/auth/reset-password`, { token, new_password }),
};

// Documents API
export const documentsApi = {
  list: (status) => axios.get(`${API_URL}/documents`, { params: { status } }),
  get: (id) => axios.get(`${API_URL}/documents/${id}`),
  send: (data) => axios.post(`${API_URL}/documents/send`, data),
  batchSend: (data) => axios.post(`${API_URL}/documents/batch-send`, data),
  bulkOperation: (documentIds, operation) => axios.post(`${API_URL}/documents/bulk-operation`, { document_ids: documentIds, operation }),
  approve: (id) => axios.post(`${API_URL}/documents/${id}/approve`),
  reject: (id, reason) => axios.post(`${API_URL}/documents/${id}/reject`, null, { params: { reason } }),
  linkBullhorn: (id, bullhornId, entityType) => axios.post(`${API_URL}/documents/${id}/link-bullhorn`, { bullhorn_id: bullhornId, entity_type: entityType }),
  cancel: (id) => axios.delete(`${API_URL}/documents/${id}`),
  permanentDelete: (id) => axios.delete(`${API_URL}/documents/${id}/permanent`),
  getAuditCertificate: (id) => axios.get(`${API_URL}/documents/${id}/audit-certificate`),
  getPreviewPdf: (id) => axios.get(`${API_URL}/documents/${id}/preview-pdf`),
  regeneratePdf: (id) => axios.post(`${API_URL}/documents/${id}/regenerate-completed-pdf`),
  resyncPdf: (id) => axios.post(`${API_URL}/documents/${id}/resync-pdf`),
};

// Public Document API (no auth required)
export const publicApi = {
  getDocument: (token) => axios.get(`${API_URL}/complete/${token}`),
  submitDocument: (token, data) => axios.post(`${API_URL}/complete/${token}`, data),
  getDocumentsByEmail: (email, tenantId) => {
    const params = tenantId ? { tenant_id: tenantId } : undefined;
    return axios.get(`${API_URL}/documents-by-email/${encodeURIComponent(email)}`, { params });
  },
  getDocumentsByPortalToken: (token) => axios.get(`${API_URL}/portal/access/${encodeURIComponent(token)}`),
};

// Bullhorn API
export const bullhornApi = {
  status: () => axios.get(`${API_URL}/bullhorn/status`),
  authenticate: () => axios.post(`${API_URL}/bullhorn/auth`),
  searchCandidates: (query) => axios.get(`${API_URL}/bullhorn/candidates/search`, { params: { query } }),
  searchContacts: (query) => axios.get(`${API_URL}/bullhorn/contacts/search`, { params: { query } }),
  searchCompanies: (query) => axios.get(`${API_URL}/bullhorn/companies/search`, { params: { query } }),
  searchPlacements: (query) => axios.get(`${API_URL}/bullhorn/placements/search`, { params: { query } }),
  getContact: (id) => axios.get(`${API_URL}/bullhorn/contacts/${id}`),
  getCompany: (id) => axios.get(`${API_URL}/bullhorn/companies/${id}`),
  getPlacement: (id) => axios.get(`${API_URL}/bullhorn/placements/${id}`),
  syncDocument: (documentId) => axios.post(`${API_URL}/bullhorn/sync/${documentId}`),
  getEntityFields: (entityType) => axios.get(`${API_URL}/bullhorn/entity-fields/${entityType}`),
  getFileTypes: (entityType = 'candidate') => axios.get(`${API_URL}/bullhorn/file-types`, { params: { entity_type: entityType } }),
  getCredentials: (search) => axios.get(`${API_URL}/bullhorn/credentials`, { params: search ? { search } : {} }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API_URL}/dashboard/stats`),
  getAnalytics: (days = 30) => axios.get(`${API_URL}/analytics`, { params: { days } }),
};

// Email Templates API
export const emailTemplatesApi = {
  list: () => axios.get(`${API_URL}/email-templates`),
  get: (id) => axios.get(`${API_URL}/email-templates/${id}`),
  create: (data) => axios.post(`${API_URL}/email-templates`, data),
  update: (id, data) => axios.put(`${API_URL}/email-templates/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/email-templates/${id}`),
  getDefaults: () => axios.get(`${API_URL}/email-templates/defaults/all`),
};

// Packets API
export const packetsApi = {
  list: () => axios.get(`${API_URL}/packets`),
  get: (id) => axios.get(`${API_URL}/packets/${id}`),
  create: (data) => axios.post(`${API_URL}/packets`, data),
  update: (id, data) => axios.put(`${API_URL}/packets/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/packets/${id}`),
  send: (data) => axios.post(`${API_URL}/packets/send`, data),
  // Dry-run: render preview PDFs for each template in the packet without
  // creating documents or sending emails. Same payload shape as `send`.
  testPreview: (data) => axios.post(`${API_URL}/packets/test-preview`, data),
};

// Recipients API
export const recipientsApi = {
  list: (params) => axios.get(`${API_URL}/recipients`, { params }),
  get: (email) => axios.get(`${API_URL}/recipients/${encodeURIComponent(email)}`),
  sendReminder: (data) => axios.post(`${API_URL}/recipients/send-reminder`, data),
};

// Company Settings API
export const settingsApi = {
  // SettingsPage (authenticated) reads its OWN tenant's settings via /me.
  // The plain /settings/company endpoint is reserved for the candidate
  // portal (public, requires ?tenant_id=...).
  getCompany: () => axios.get(`${API_URL}/settings/company/me`),
  updateCompany: (data) => axios.put(`${API_URL}/settings/company`, data),
  sendEmailPreview: (templateType) => axios.post(`${API_URL}/settings/email/preview`, { template_type: templateType }),
  autoDetect: (websiteUrl) => axios.post(`${API_URL}/settings/auto-detect`, { website_url: websiteUrl }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/settings/company/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateUserRole: (tenantId, userId, role) =>
    axios.put(`${API_URL}/tenants/${tenantId}/users/${userId}/role?role=${role}`),
};

// Health check
export const healthCheck = () => axios.get(`${API_URL}/health`);

// Online Forms API (Jotform-style) — feature-flagged behind
// `tenant.settings.enable_online_forms`. All endpoints 403 when the
// feature is off, so callers can use that to hide the UI.
export const formsApi = {
  list: () => axios.get(`${API_URL}/forms`),
  get: (id) => axios.get(`${API_URL}/forms/${id}`),
  create: (data) => axios.post(`${API_URL}/forms`, data),
  update: (id, data) => axios.put(`${API_URL}/forms/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/forms/${id}`),
  send: (data) => axios.post(`${API_URL}/forms/send`, data),
  listSubmissions: (params) => axios.get(`${API_URL}/forms/submissions/list`, { params }),
  getSubmission: (id) => axios.get(`${API_URL}/forms/submissions/${id}`),
  resyncSubmission: (id) => axios.post(`${API_URL}/forms/submissions/${id}/resync`),
  resendSubmission: (id) => axios.post(`${API_URL}/forms/submissions/${id}/resend`),
  cancelSubmission: (id) => axios.post(`${API_URL}/forms/submissions/${id}/cancel`),
  bulkSubmissions: (action, submissionIds) => axios.post(`${API_URL}/forms/submissions/bulk`, { action, submission_ids: submissionIds }),
  toggleFeature: (enable) => axios.post(`${API_URL}/forms/feature/toggle`, { enable }),
  updateFeatures: (payload) => axios.patch(`${API_URL}/forms/features`, payload),
  generateWithAi: (data) => axios.post(`${API_URL}/forms/generate`, data),
  dashboardStats: () => axios.get(`${API_URL}/forms/dashboard/stats`),
};

// Public Forms API — candidate-facing, no auth header needed.
export const publicFormsApi = {
  open: (token) => axios.get(`${API_URL}/public/forms/access/${token}`),
  submit: (token, payload) => axios.post(`${API_URL}/public/forms/submit/${token}`, payload),
};

// Tenants API
export const tenantsApi = {
  getCurrent: () => axios.get(`${API_URL}/tenants/current`),
  get: (id) => axios.get(`${API_URL}/tenants/${id}`),
  update: (id, data) => axios.put(`${API_URL}/tenants/${id}`, data),
  updateSettings: (id, data) => axios.put(`${API_URL}/tenants/${id}/settings`, data),
  listUsers: (id, params) => axios.get(`${API_URL}/tenants/${id}/users`, { params }),
  inviteUser: (id, email, name, role = 'user') => axios.post(`${API_URL}/tenants/${id}/users/invite`, null, {
    params: { email, name, role }
  }),
  updateUserRole: (tenantId, userId, role) => axios.put(`${API_URL}/tenants/${tenantId}/users/${userId}/role`, null, {
    params: { role }
  }),
  removeUser: (tenantId, userId) => axios.delete(`${API_URL}/tenants/${tenantId}/users/${userId}`),
  // Bullhorn config for tenant
  saveBullhornConfig: (tenantId, config) => axios.put(`${API_URL}/tenants/${tenantId}/bullhorn-config`, config),
  getBullhornConfig: (tenantId) => axios.get(`${API_URL}/tenants/${tenantId}/bullhorn-config`),
  connectBullhorn: (tenantId) => axios.post(`${API_URL}/tenants/${tenantId}/bullhorn-connect`),
  autoConnectBullhorn: (tenantId) => axios.post(`${API_URL}/tenants/${tenantId}/bullhorn-auto-connect`),
};
